import type { ThemeConfig } from 'antd';

/**
 * Govard CRM — Ant Design v6 Theme
 *
 * Warm amber/stone palette. Single source of truth for all color/spacing/radius tokens.
 * Applied via ConfigProvider in AppLayout.tsx — never override here inline in components.
 */
export const govardTheme: ThemeConfig = {
  token: {
    // ─── Brand: Warm Amber ────────────────────────────────────────────────
    colorPrimary: '#d97706',
    colorSuccess: '#16a34a',
    colorWarning: '#ea580c',
    colorError: '#dc2626',
    colorInfo: '#2563eb',

    // Primary state variants (antd derives most, but explicit for clarity)
    colorPrimaryBg: '#fffbeb',
    colorPrimaryBgHover: '#fef3c7',
    colorPrimaryBorder: '#fbbf24',
    colorPrimaryBorderHover: '#f59e0b',
    colorPrimaryHover: '#b45309',
    colorPrimaryActive: '#92400e',
    colorPrimaryText: '#d97706',
    colorPrimaryTextHover: '#b45309',
    colorPrimaryTextActive: '#92400e',

    // ─── Typography: Stone palette ────────────────────────────────────────
    colorTextBase: '#44403c',
    colorText: '#44403c',
    colorTextSecondary: '#78716c',
    colorTextTertiary: '#a8a29e',
    colorTextQuaternary: '#d6d3d1',
    colorTextDisabled: '#d6d3d1',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    fontFamilyCode: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: 13,

    // ─── Surfaces: Warm cream ─────────────────────────────────────────────
    colorBgBase: '#fefbf6',
    colorBgContainer: '#fefbf6',
    colorBgElevated: '#fffdfa',
    colorBgLayout: '#f9f7f4',
    colorBgSpotlight: 'rgba(68, 64, 60, 0.85)',
    colorBgMask: 'rgba(68, 64, 60, 0.45)',

    // Semantic bg/border variants
    colorSuccessBg: '#f0fdf4',
    colorSuccessBgHover: '#dcfce7',
    colorSuccessBorder: '#86efac',
    colorSuccessBorderHover: '#4ade80',
    colorSuccessHover: '#15803d',
    colorSuccessActive: '#166534',
    colorSuccessText: '#16a34a',
    colorSuccessTextHover: '#15803d',
    colorSuccessTextActive: '#166534',

    colorWarningBg: '#fff7ed',
    colorWarningBgHover: '#fed7aa',
    colorWarningBorder: '#fdba74',
    colorWarningBorderHover: '#fb923c',
    colorWarningHover: '#c2410c',
    colorWarningActive: '#9a3412',
    colorWarningText: '#ea580c',
    colorWarningTextHover: '#c2410c',
    colorWarningTextActive: '#9a3412',

    colorErrorBg: '#fef2f2',
    colorErrorBgHover: '#fecaca',
    colorErrorBorder: '#fca5a5',
    colorErrorBorderHover: '#f87171',
    colorErrorHover: '#b91c1c',
    colorErrorActive: '#991b1b',
    colorErrorText: '#dc2626',
    colorErrorTextHover: '#b91c1c',
    colorErrorTextActive: '#991b1b',

    colorInfoBg: '#eff6ff',
    colorInfoBgHover: '#dbeafe',
    colorInfoBorder: '#93c5fd',
    colorInfoBorderHover: '#60a5fa',
    colorInfoHover: '#1d4ed8',
    colorInfoActive: '#1e40af',
    colorInfoText: '#2563eb',
    colorInfoTextHover: '#1d4ed8',
    colorInfoTextActive: '#1e40af',

    // ─── Borders ─────────────────────────────────────────────────────────
    colorBorder: '#e7e5e4',
    colorBorderSecondary: '#f5f5f4',

    // ─── Geometry ─────────────────────────────────────────────────────────
    borderRadius: 12,
    borderRadiusXS: 4,
    borderRadiusSM: 8,
    borderRadiusLG: 16,

    // ─── Spacing ──────────────────────────────────────────────────────────
    padding: 20,
    paddingSM: 16,
    paddingLG: 28,
    margin: 20,
    marginSM: 16,
    marginLG: 28,

    // ─── Shadows ──────────────────────────────────────────────────────────
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)',
    boxShadowSecondary: '0 8px 20px 0 rgba(0, 0, 0, 0.08)',
  },

  components: {
    Layout: {
      siderBg: '#f5f1eb',
      headerBg: '#fffdfa',
      bodyBg: '#f9f7f4',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: '#fffbeb',
      itemSelectedColor: '#d97706',
      itemHoverBg: '#f5f5f4',
    },
    Table: {
      headerBg: '#f9f7f4',
      headerColor: '#78716c',
      rowHoverBg: '#f5f5f4',
      borderColor: '#e7e5e4',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      headerSplitColor: 'transparent',
    },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Input: {
      activeShadow: '0 0 0 2px rgba(217, 119, 6, 0.1)',
    },
    Card: {
      headerBg: 'transparent',
    },
    Tabs: {
      inkBarColor: '#d97706',
      itemSelectedColor: '#d97706',
      itemHoverColor: '#b45309',
    },
    Statistic: {
      contentFontSize: 28,
    },
  },
};
